import {
  createLegalAidApplicationStandalone,
  getLegalAidApplicationStandalone,
  invokeFunctionStandalone,
  invokeLlmStandalone,
  listLegalAidApplicationsStandalone,
  sendEmailStandalone,
  updateLegalAidApplicationStandalone,
  uploadFileStandalone,
  listConflictNamesStandalone,
  extractDataFromUploadedFileStandalone,
} from "@/lib/standaloneServices";

// Kept in step with MAX_UPLOAD_BYTES in api/_storage.js, which is the limit that
// actually binds - along with the bucket's own file_size_limit. A test asserts the two
// numbers agree, because a client that thinks the ceiling is higher than the server does
// produces a failed upload with a confusing message.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

// Standalone mode only: the file is inlined as a data URL, so this stays small.
const LOCAL_INLINE_UPLOAD_LIMIT_BYTES = 150 * 1024;

const LOCAL_USER_KEY = "deadline_guard_local_user";

const APP_ENV = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : {};
const INTEGRATION_MODE = APP_ENV.VITE_APP_INTEGRATION_MODE || "standalone";
const getApiBaseUrl = () => {
  return ""; // Relative URLs route through Vite proxy in dev and same-origin in prod
};
const API_BASE_URL = getApiBaseUrl();

function isHostedPortal() {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return INTEGRATION_MODE === "remote"
    || hostname === "portal.completelawsupport.com"
    || hostname.endsWith(".vercel.app");
}

function getStoredUser() {
  try {
    const stored = window.localStorage.getItem(LOCAL_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

async function readJson(response) {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return null;
  }
  return response.json();
}

async function sendBackendEmail(payload) {
  const response = await fetch(`${API_BASE_URL}/api/gmail/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const json = await readJson(response);
    throw new Error(json?.error || "Email send failed");
  }

  return (await readJson(response)) || { ok: true };
}

// Local client JWT authentication is injected automatically by apiFetch

async function postJson(path, payload) {
  let url = `${API_BASE_URL}${path}`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = await readJson(response);
  if (!response.ok || json?.ok === false) {
    throw new Error(json?.error || `Request failed: ${path}`);
  }
  return json;
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

// Every remote entity call below falls back to the browser-local `standaloneServices`
// store when the request fails. That is the intended behaviour for the packaged local
// install, but on the hosted portal it means a record the user believes was saved to the
// shared database only ever reached this browser. `reportDegradedSave` makes that visible
// instead of letting the call return as if it succeeded.
//
// Read fallbacks are reported quietly (stale data is recoverable by reloading); write
// fallbacks are reported as errors because the record did not reach the shared system.
function reportDegradedSave(operation, err, { isWrite = false } = {}) {
  console.warn(`Remote ${operation} failed, falling back to this browser's local copy.`, err);
  if (!isHostedPortal()) return; // Local install: offline-first is the supported mode.
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("cls:degraded-storage", {
    detail: { operation, isWrite, message: err?.message || String(err || "") },
  }));
}

async function apiFetch(url, options = {}) {
  const token = window.localStorage.getItem("clsp_session_token");
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timeout = 15000; // 15 seconds timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await window.fetch(url, {
      ...options,
      headers,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (err) {
    clearTimeout(timeoutId);
    console.warn("API request failed or timed out:", err);
    throw err;
  }
}

const fetch = apiFetch;

function isRemoteMode() {
  // Always true to interact with local Express/SQLite backend
  return true;
}

export const base44 = {
  versions: {
    async list(recordId) {
      if (isRemoteMode()) {
        const response = await fetch(`${API_BASE_URL}/api/versions/${encodeURIComponent(recordId)}`);
        if (!response.ok) throw new Error("Failed to fetch version history");
        return await response.json();
      }
      return [];
    },
    async get(recordId, versionNumber) {
      if (isRemoteMode()) {
        const response = await fetch(`${API_BASE_URL}/api/versions/${encodeURIComponent(recordId)}/${versionNumber}`);
        if (!response.ok) throw new Error("Failed to get version snapshot");
        return await response.json();
      }
      return null;
    },
    async restore(recordId, versionNumber) {
      if (isRemoteMode()) {
        const response = await fetch(`${API_BASE_URL}/api/versions/${encodeURIComponent(recordId)}/${versionNumber}/restore`, {
          method: "POST"
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || "Failed to restore version");
        }
        return await response.json();
      }
      return { ok: false, error: "Not supported in standalone mode" };
    }
  },
  auth: {
    async me() {
      if (isHostedPortal()) {
        const response = await window.fetch(`${API_BASE_URL}/api/auth/me`, {
          credentials: "same-origin",
          cache: "no-store",
        });
        if (response.status === 401) return null;
        if (!response.ok) throw new Error("Failed to verify session");
        const data = await response.json();
        return data.user || null;
      }
      const token = window.localStorage.getItem("clsp_session_token");
      if (!token) return null;
      try {
        const response = await window.fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (!response.ok) {
          throw new Error("Session expired");
        }
        return await response.json();
      } catch (err) {
        console.warn("Session check failed, clearing token:", err);
        window.localStorage.removeItem("clsp_session_token");
        window.localStorage.removeItem(LOCAL_USER_KEY);
        return null;
      }
    },
    async login(email, password) {
      const response = await window.fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email, password })
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Login failed");
      }
      const data = await response.json();
      if (data.token) {
        // Retained only for the packaged local server. Production sessions are
        // kept in HTTP-only cookies and never exposed to application JavaScript.
        window.localStorage.setItem("clsp_session_token", data.token);
      }
      window.localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(data.user));
      return data.user;
    },
    async register(email, password, fullName, role) {
      const response = await window.fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, fullName, role })
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Registration failed");
      }
      const data = await response.json();
      window.localStorage.setItem("clsp_session_token", data.token);
      window.localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(data.user));
      return data.user;
    },
    async updateMe(updates = {}) {
      const nextUser = {
        ...getStoredUser(),
        ...updates,
      };
      window.localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(nextUser));
      return nextUser;
    },
    async resetPassword(currentPassword, newPassword) {
      const token = window.localStorage.getItem("clsp_session_token");
      const response = await window.fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Password reset failed");
      }
      return await response.json();
    },
    async recoveryReset(email, recoveryKey, newPassword) {
      const response = await window.fetch(`${API_BASE_URL}/api/auth/recovery-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, recoveryKey, newPassword })
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Emergency recovery reset failed");
      }
      return await response.json();
    },
    async adminResetUserPassword(userEmail, newPassword) {
      const token = window.localStorage.getItem("clsp_session_token");
      const response = await window.fetch(`${API_BASE_URL}/api/admin/reset-user-password`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ userEmail, newPassword })
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Admin password reset failed");
      }
      return await response.json();
    },
    async logout(redirectUrl) {
      try {
        const token = window.localStorage.getItem("clsp_session_token");
        if (token) {
          await window.fetch(`${API_BASE_URL}/api/auth/logout`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              ...(token ? { "Authorization": `Bearer ${token}` } : {})
            }
          });
        }
      } catch (err) {
        console.warn("Logout request failed:", err);
      }
      window.localStorage.removeItem("clsp_session_token");
      window.localStorage.removeItem(LOCAL_USER_KEY);
      if (redirectUrl) {
        window.location.assign(redirectUrl);
      } else {
        window.location.reload();
      }
    },
    redirectToLogin(redirectUrl) {
      // In local mode we don't redirect away; AuthProvider triggers the Login screen overlay
      console.log("Redirect to login requested");
    },
  },
  setup: {
    async status() {
      if (isHostedPortal()) {
        return { initialized: true };
      }
      const response = await window.fetch(`${API_BASE_URL}/api/setup/status`);
      if (!response.ok) throw new Error("Failed to check setup status");
      return await response.json();
    },
    async initialize(payload) {
      const response = await window.fetch(`${API_BASE_URL}/api/setup/initialize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Setup initialization failed");
      }
      const data = await response.json();
      window.localStorage.setItem("clsp_session_token", data.token);
      window.localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(data.user));
      return data.user;
    }
  },
  admin: {
    async auditLogs(filters = {}) {
      const params = new URLSearchParams();
      if (filters.user) params.append("user", filters.user);
      if (filters.matter) params.append("matter", filters.matter);
      if (filters.action) params.append("action", filters.action);
      if (filters.date_start) params.append("date_start", filters.date_start);
      if (filters.date_end) params.append("date_end", filters.date_end);

      const response = await fetch(`${API_BASE_URL}/api/admin/audit-logs?${params.toString()}`);
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to fetch audit logs");
      }
      return await response.json();
    },
    async logEvent(payload) {
      try {
        if (INTEGRATION_MODE === "standalone") {
          console.log("[Standalone LogEvent]", payload);
          return { ok: true };
        }
        return await postJson("/api/app/log-event", payload);
      } catch (err) {
        console.warn("Failed to log event, falling back to local console log", err);
        console.log("[Fallback LogEvent]", payload);
        return { ok: true };
      }
    },
    license: {
      async status() {
        const response = await fetch(`${API_BASE_URL}/api/admin/license`);
        if (!response.ok) throw new Error("Failed to fetch license details");
        return await response.json();
      },
      async activate(licenseKey) {
        return await postJson("/api/admin/license/activate", { licenseKey });
      },
      async deactivate() {
        return await postJson("/api/admin/license/deactivate");
      }
    },
    updates: {
      async check() {
        const response = await fetch(`${API_BASE_URL}/api/admin/updates/check`);
        if (!response.ok) throw new Error("Failed to check for updates");
        return await response.json();
      },
      async install() {
        return await postJson("/api/admin/updates/install");
      }
    },
    backup: {
      async status() {
        const response = await fetch(`${API_BASE_URL}/api/admin/backup/status`);
        if (!response.ok) throw new Error("Failed to fetch backup status");
        return await response.json();
      },
      async manual() {
        return await postJson("/api/admin/backup");
      },
      async verify(type, filename) {
        return await postJson("/api/admin/backup/verify", { type, filename });
      },
      async restore(type, filename) {
        return await postJson("/api/admin/backup/restore", { type, filename });
      }
    },
    async healthCheck() {
      const response = await fetch(`${API_BASE_URL}/api/admin/health`);
      if (!response.ok) throw new Error("Health check failed");
      return await response.json();
    }
  },
  matters: {
    async list() {
      const response = await fetch(`${API_BASE_URL}/api/app/matters`);
      if (!response.ok) throw new Error("Failed to fetch matters");
      return await response.json();
    },
    async get(id) {
      const response = await fetch(`${API_BASE_URL}/api/app/matters/${encodeURIComponent(id)}`);
      if (!response.ok) throw new Error(`Failed to fetch matter ${id}`);
      return await response.json();
    },
    async create(payload) {
      return await postJson("/api/app/matters", payload);
    },
    async update(id, payload) {
      const response = await fetch(`${API_BASE_URL}/api/app/matters/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to update matter");
      }
      return await response.json();
    },
    tasks: {
      async create(id, taskTitle) {
        return await postJson(`/api/app/matters/${encodeURIComponent(id)}/tasks`, { taskTitle });
      },
      async toggle(id, taskId, status) {
        const response = await fetch(`${API_BASE_URL}/api/app/matters/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status })
        });
        if (!response.ok) throw new Error("Failed to update task status");
        return await response.json();
      },
      async delete(id, taskId) {
        const response = await fetch(`${API_BASE_URL}/api/app/matters/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}`, {
          method: "DELETE"
        });
        if (!response.ok) throw new Error("Failed to delete task");
        return await response.json();
      }
    },
    async search(query) {
      let url = `${API_BASE_URL}/api/matters`;
      if (query) url += `?q=${encodeURIComponent(query)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to search matters");
      return await response.json();
    }
  },
  appLogs: {
    async logUserInApp() {
      return { ok: true };
    },
  },
  functions: {
    async invoke(name, payload = {}) {
      if (isRemoteMode()) {
        try {
          const json = await postJson(`/api/app/functions/${encodeURIComponent(name)}`, payload);
          // If we expect barristers but the backend returned an empty/invalid array, trigger the fallback to standalone data
          const isBarristerFunction = name.startsWith("get") && (name.endsWith("List") || name.endsWith("Barristers") || name.includes("Chapmans"));
          if (isBarristerFunction && (!json || !Array.isArray(json.barristers) || json.barristers.length === 0)) {
            throw new Error(`Backend returned empty or invalid barristers for ${name}`);
          }
          return { data: { ...json, ok: undefined } };
        } catch (err) {
          const allowsStaticDirectoryFallback = name.startsWith("get")
            && (name.endsWith("List") || name.endsWith("Barristers") || name.includes("Chapmans"));
          if (allowsStaticDirectoryFallback) {
            console.warn(`Remote directory ${name} is unavailable. Using the bundled directory.`, err);
            return { data: await invokeFunctionStandalone(name, payload) };
          }
          throw err;
        }
      }
      return { data: await invokeFunctionStandalone(name, payload) };
    },
  },
  entities: {
    LegalAidApplication: {
      async list(sort, limit) {
        return listLegalAidApplicationsStandalone(sort, limit);
      },
      async get(id) {
        return getLegalAidApplicationStandalone(id);
      },
      async create(payload) {
        return createLegalAidApplicationStandalone(payload);
      },
      async update(id, payload) {
        return updateLegalAidApplicationStandalone(id, payload);
      },
    },
    ConflictName: {
      async list(sort, limit) {
        return listConflictNamesStandalone(sort, limit);
      },
    },
  },
  integrations: {
    Core: {
      async SendEmail({ to, cc, subject, body, ...rest }) {
        if (isRemoteMode()) {
          return sendBackendEmail({ to, cc, subject, body, ...rest });
        }
        return sendEmailStandalone({ to, cc, subject, body, ...rest });
      },
      // Uploads a file to private object storage and returns a durable reference.
      //
      // This used not to upload anything: it read the file into a base64 data URL and the
      // caller stored that string inline in a database row, which is why the ceiling was
      // 1.5 MB - below a scanned police brief or multi-page discovery PDF, so real
      // evidence uploads simply failed. Files now go to the `portal-uploads` bucket and
      // the record holds /api/files?path=..., resolved on each read.
      //
      // Records written before this change still hold data URLs. Nothing needs migrating:
      // those strings keep rendering exactly as they did, and only new uploads take the
      // storage path.
      async UploadFile({ file }) {
        if (!file || typeof file.size !== "number") {
          throw new Error("Choose a valid file to upload.");
        }

        // Standalone mode has no storage behind it, so it keeps the old inline
        // data-URL behaviour for the fictional-data demo. The small ceiling stays
        // there because in that mode the string really does end up inline.
        if (!isRemoteMode()) {
          if (file.size > LOCAL_INLINE_UPLOAD_LIMIT_BYTES) {
            throw new Error("File is too large. The maximum supported size is 150 KB.");
          }
          return { file_url: await fileToDataUrl(file) };
        }

        if (file.size > MAX_UPLOAD_BYTES) {
          const limitMb = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));
          throw new Error(`File is too large. The maximum supported size is ${limitMb} MB.`);
        }

        // Two steps on purpose. The API authenticates the caller and hands back a
        // one-shot signed URL; the bytes then go straight from the browser to object
        // storage. Sending the file through the API instead would cap it at the
        // platform's request-body limit, which is barely above the ceiling this
        // replaced. The server re-checks type and size, and so does the bucket.
        const ticket = await postJson("/api/upload", {
          name: file.name,
          content_type: file.type,
          size: file.size,
        });

        const stored = await fetch(ticket.upload_url, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!stored.ok) {
          throw new Error("The file could not be stored. Check your connection and try again.");
        }

        // file_url is a durable reference resolved through /api/files on every read,
        // not a signed URL. A signed URL written into a record would expire and break
        // the attachment on a matter the firm may need to produce years later.
        return { file_url: ticket.file_url, storage_path: ticket.storage_path };
      },
      async InvokeLLM(payload = {}) {
        if (isRemoteMode()) {
          try {
            const json = await postJson("/api/app/invoke-llm", payload);
            return json.result;
          } catch (err) {
            console.warn("Remote InvokeLLM failed. Falling back to standalone local processing.", err);
          }
        }
        return invokeLlmStandalone(payload);
      },
      /**
       * @param {any} payload
       * @param {any} [onProgress]
       */
      async ExtractDataFromUploadedFile(payload = {}, onProgress = null) {
        if (isRemoteMode()) {
          try {
            return await postJson("/api/app/extract-data", payload);
          } catch (err) {
            console.warn("Remote ExtractDataFromUploadedFile failed. Falling back to standalone local extraction.", err);
          }
        }
        return extractDataFromUploadedFileStandalone(payload, onProgress);
      },
    },
  },
};

// Intercept browser print window calls to log print events automatically
if (typeof window !== "undefined") {
  const originalPrint = window.print;
  window.print = function(...args) {
    let matterId = null;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      matterId = urlParams.get("matter_id") || urlParams.get("id") || urlParams.get("matter");
      if (!matterId) {
        const pathParts = window.location.pathname.split('/');
        const mIndex = pathParts.findIndex(p => p === 'matters' || p === 'audit');
        if (mIndex !== -1 && pathParts[mIndex + 1]) {
          matterId = pathParts[mIndex + 1];
        }
      }
    } catch (e) {
      console.warn("Failed to extract matter ID from URL for print log:", e);
    }

    base44.admin.logEvent({
      action: "print_report",
      entityType: "report",
      entityId: matterId,
      matterId: matterId,
      details: `Printed page: ${document.title || window.location.pathname}`
    }).catch(err => console.error("Failed to log print event:", err));

    return originalPrint.apply(this, args);
  };
}
